import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AssignmentService, ApplicationHistory } from '../Assignment/assignment.service';
import { AssessmentJob, AssessmentJobType } from '../../services/ai-assessment.service';
import { UserContextService } from '../../services/common/user-context.service';
import { Role } from '../../models/authorization/role.enum';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../material.module';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { UtcDatePipe } from '../../pipes/utc-date.pipe';

export interface ApplicationHistoryDialogData {
    appId: string;
    appName?: string;
    aiJobs?: AssessmentJob[];
}

@Component({
    selector: 'app-application-history-dialog',
    templateUrl: './application-history-dialog.component.html',
    styleUrls: ['./application-history-dialog.component.css'],
    imports: [CommonModule, MaterialModule, SpinnerComponent, UtcDatePipe]
})
export class ApplicationHistoryDialogComponent implements OnInit {
    applicationHistory: ApplicationHistory[] = [];
    isLoading: boolean = false;
    error: string = '';
    assignmentHistoryLoaded = false;
    isEnterpriseLead: boolean = false;

    constructor(
        public dialogRef: MatDialogRef<ApplicationHistoryDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ApplicationHistoryDialogData,
        private assignmentService: AssignmentService,
        private userContextService: UserContextService
    ) { }

    ngOnInit(): void {
        this.isEnterpriseLead = this.userContextService.userRoleSubject.value.includes(Role.EnterpriseLead);
        this.loadApplicationHistory();
    }

    loadApplicationHistory(): void {
        if (this.assignmentHistoryLoaded) return;
        this.isLoading = true;
        this.error = '';
        this.assignmentService.getApplicationHistory(this.data.appId).subscribe({
            next: (history) => {
                this.applicationHistory = history;
                this.isLoading = false;
                this.assignmentHistoryLoaded = true;
            },
            error: (err) => {
                this.error = 'Failed to load history';
                this.isLoading = false;
                console.error('Error loading history:', err);
            }
        });
    }

    onClose(): void {
        this.dialogRef.close();
    }

    selectAiJob(job: AssessmentJob): void {
        this.dialogRef.close({ selectedAiJobId: job.id });
    }

    getStatusLabel(job: AssessmentJob): string {
        const s = job.status as any;
        if (s === 'Pending'   || s === 0 || s === '0') return 'Pending';
        if (s === 'Running'   || s === 1 || s === '1') return 'Running';
        if (s === 'Completed' || s === 2 || s === '2') return 'Completed';
        if (s === 'Failed'    || s === 3 || s === '3') return 'Failed';
        return String(s);
    }

    getJobTypeLabel(job: AssessmentJob): string {
        return this.isLightweightJob(job) ? 'Lightweight' : 'Full Code';
    }

    isLightweightJob(job: AssessmentJob): boolean {
        return job.jobType === AssessmentJobType.LightweightCodeValidation
            || job.jobType === 'LightweightCodeValidation'
            || job.jobType === '1'
            || (job.jobType as any) === 1;
    }

    getJobScore(job: AssessmentJob): number | null {
        return this.isLightweightJob(job)
            ? job.lightweightResult?.score ?? job.totalScore ?? null
            : job.totalScore ?? job.result?.totalScore ?? null;
    }

    isJobCompleted(job: AssessmentJob): boolean {
        return job.status === 'Completed' || (job.status as any) === 2 || job.status === '2';
    }

    isJobFailed(job: AssessmentJob): boolean {
        return job.status === 'Failed' || (job.status as any) === 3 || job.status === '3';
    }

    isJobRunning(job: AssessmentJob): boolean {
        return job.status === 'Pending' || job.status === 'Running' ||
            (job.status as any) === 0 || (job.status as any) === 1 ||
            job.status === '0' || job.status === '1';
    }
}
